import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, CheckCircle2, Barcode, Phone, ArrowLeft, User, MapPin, HardHat, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import type { SellerRegistrationStatus } from '@m8-app/contracts';
import { registerDevice, listInstallers } from '../api/seller.js';
import { Button } from '../components/ui/Button.js';
import { StatusBadge } from '../components/ui/Badge.js';
import { CopyButton } from '../components/ui/CopyButton.js';
import { cn } from '../components/ui/cn.js';
import { useFormDraft } from '../lib/useFormDraft.js';

/** One form for both lighting products — only the product type, the copy and
 *  the draft slot differ. The backend rejects a serial whose prefix does not
 *  match the product (PRODUCT_TYPE_MISMATCH), so each sidebar entry gets its
 *  own route rather than letting an HCL- serial go out as 'm8'. */
const LIGHTING_PRODUCTS = {
  m8:    { title: 'Register M8 Light Controller', kind: 'stair light',   serialHint: 'Must be an M8 (stair light) device serial (HM8-…).',    draftKey: 'seller.draft.m8' },
  avira: { title: 'Register Avira Controller',    kind: 'ceiling light', serialHint: 'Must be an Avira (ceiling light) device serial (HCL-…).', draftKey: 'seller.draft.avira' },
} as const;
export type LightingProduct = keyof typeof LIGHTING_PRODUCTS;
const EMPTY_DRAFT = {
  serialNumber:        '',
  customerName:        '',
  customerMobile:      '',
  customerAddress:     '',
  selectedInstallerId: '',
  installerName:       '',
  installerMobile:     '',
};

type WizardStep = 'details' | 'confirm' | 'success';
const STEPS = [
  { n: 1, key: 'details' as WizardStep, label: 'Details' },
  { n: 2, key: 'confirm' as WizardStep, label: 'Confirm' },
  { n: 3, key: 'success' as WizardStep, label: 'Done'    },
];
const STEP_INDEX: Record<WizardStep, number> = { details: 1, confirm: 2, success: 3 };

function StepIndicator({ step }: { step: WizardStep }) {
  const current = STEP_INDEX[step];
  return (
    <div className="flex items-center mb-8">
      {STEPS.map((s, i) => (
        <div key={s.n} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center gap-1.5 shrink-0">
            <div className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all',
              current > s.n  ? 'bg-teal-600 border-teal-600 text-white' :
              current === s.n ? 'border-teal-600 text-teal-600 bg-white' :
                                'border-slate-200 text-slate-400 bg-white',
            )}>
              {current > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
            </div>
            <span className={cn('text-xs font-medium', current >= s.n ? 'text-teal-600' : 'text-slate-400')}>
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={cn('flex-1 h-px mx-3 mb-5', current > s.n ? 'bg-teal-600' : 'bg-slate-200')} />
          )}
        </div>
      ))}
    </div>
  );
}

const inputCls = 'w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all';

export function RegisterM8Page({ product = 'm8' }: { product?: LightingProduct } = {}) {
  const copy = LIGHTING_PRODUCTS[product];
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const { data: installersData } = useQuery({ queryKey: ['seller-installers'], queryFn: listInstallers });
  const installersList = installersData?.installers ?? [];

  const [step, setStep] = useState<WizardStep>('details');
  const [draft, updateDraft, clearDraft] = useFormDraft(copy.draftKey, EMPTY_DRAFT);
  const [result, setResult] = useState<{ id: string; status: SellerRegistrationStatus } | null>(null);
  const { serialNumber, customerName, customerMobile, customerAddress,
          selectedInstallerId, installerName, installerMobile } = draft;

  const mutation = useMutation({
    mutationFn: registerDevice,
    onSuccess: (res) => {
      setResult({ id: res.registrationId, status: res.status });
      setStep('success');
      clearDraft();
      queryClient.invalidateQueries({ queryKey: ['seller-devices'] });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      toast.error(err.response?.data?.message ?? 'Registration failed. Please try again.');
      setStep('details');
    },
  });

  function handleReset() {
    clearDraft();
    setResult(null);
    setStep('details');
  }

  const detailsValid =
    serialNumber.trim() !== '' &&
    customerName.trim() !== '' &&
    customerMobile.trim() !== '';

  /** Backend requires E.164; accept bare Indian 10-digit input and add +91. */
  function normalizeMobile(raw: string): string {
    const v = raw.replace(/[\s-]/g, '');
    if (/^\d{10}$/.test(v)) return `+91${v}`;
    if (/^91\d{10}$/.test(v)) return `+${v}`;
    return v;
  }

  function handleConfirm() {
    mutation.mutate({
      productType:    product,
      serialNumber:   serialNumber.trim(),
      customerName:   customerName.trim(),
      customerMobile: normalizeMobile(customerMobile),
      ...(customerAddress.trim() ? { customerAddress: customerAddress.trim() } : {}),
      ...(installerName.trim()   ? { installerName:   installerName.trim()   } : {}),
      ...(installerMobile.trim() ? { installerMobile: normalizeMobile(installerMobile) } : {}),
    });
  }

  return (
    <div className="p-6 max-w-lg">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">{copy.title}</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Record-keeping registration for a {copy.kind}. Can be done before or after installation.
        </p>
      </div>

      <StepIndicator step={step} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <AnimatePresence mode="wait">

          {/* ── Step 1 — Details ── */}
          {step === 'details' && (
            <motion.form
              key="details"
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.15 }}
              onSubmit={(e) => { e.preventDefault(); setStep('confirm'); }}
              className="p-6 space-y-5"
            >
              {/* Serial */}
              <div>
                <label htmlFor="serial" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                  Serial number
                </label>
                <div className="relative">
                  <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    id="serial" type="text" maxLength={64} placeholder="CEIL-XXXXXXXX or STAIR-XXXXXXXX"
                    value={serialNumber} onChange={(e) => updateDraft({ serialNumber: e.target.value })}
                    required className={inputCls}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1.5">{copy.serialHint}</p>
              </div>

              {/* Customer name */}
              <div>
                <label htmlFor="customerName" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                  Customer name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    id="customerName" type="text" maxLength={120} placeholder="Full name"
                    value={customerName} onChange={(e) => updateDraft({ customerName: e.target.value })}
                    required className={inputCls}
                  />
                </div>
              </div>

              {/* Customer mobile */}
              <div>
                <label htmlFor="customerMobile" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                  Customer mobile
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    id="customerMobile" type="tel" placeholder="+91 98765 43210"
                    value={customerMobile} onChange={(e) => updateDraft({ customerMobile: e.target.value })}
                    required className={inputCls}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1.5">Include country code — e.g. +91…</p>
              </div>

              {/* Customer address (optional) */}
              <div>
                <label htmlFor="customerAddress" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                  Customer address <span className="text-slate-400 font-normal normal-case">(optional)</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                  <textarea
                    id="customerAddress" maxLength={500} rows={2} placeholder="Installation address"
                    value={customerAddress} onChange={(e) => updateDraft({ customerAddress: e.target.value })}
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5 -mx-6 px-6">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Installer <span className="text-slate-400 font-normal normal-case">(optional)</span>
                </p>
                <p className="text-xs text-slate-400 mb-3">
                  For records only — M8 install doesn&apos;t require pre-authorization.
                </p>

                {/* Installer dropdown */}
                <div>
                  <label htmlFor="installerSelect" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                    Installer
                  </label>
                  <div className="relative">
                    <HardHat className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
                    <select
                      id="installerSelect"
                      value={selectedInstallerId}
                      onChange={(e) => {
                        const id = e.target.value;
                        const inst = installersList.find((i) => i.id === id);
                        updateDraft({
                          selectedInstallerId: id,
                          installerName:   inst ? inst.name   : '',
                          installerMobile: inst ? inst.mobile : '',
                        });
                      }}
                      className="w-full pl-9 pr-8 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all appearance-none"
                    >
                      <option value="">— None / not yet assigned —</option>
                      {installersList.map((inst) => (
                        <option key={inst.id} value={inst.id}>{inst.name}{inst.serviceArea ? ` — ${inst.serviceArea}` : ''}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">Select from registered installers.</p>
                  <p className="text-xs text-slate-500 mt-1.5"><span className="font-semibold">Mobile:</span> {installerMobile || '—'}</p>
                </div>
              </div>

              <Button type="submit" fullWidth disabled={!detailsValid}>
                Continue →
              </Button>
            </motion.form>
          )}

          {/* ── Step 2 — Confirm ── */}
          {step === 'confirm' && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.15 }}
              className="p-6 space-y-5"
            >
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Review details</p>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  <ReviewRow label="Serial number" mono value={serialNumber} />
                  <ReviewRow label="Customer name" value={customerName} />
                  <ReviewRow label="Customer mobile" value={customerMobile} />
                  {customerAddress && <ReviewRow label="Customer address" value={customerAddress} />}
                  {installerName   && <ReviewRow label="Installer name"   value={installerName}   />}
                  {installerMobile && <ReviewRow label="Installer mobile" value={installerMobile} />}
                </div>
              </div>

              <Button type="button" fullWidth loading={mutation.isPending} onClick={handleConfirm}>
                Confirm &amp; Register
              </Button>
              <Button type="button" variant="ghost" fullWidth disabled={mutation.isPending}
                onClick={() => setStep('details')}>
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
            </motion.div>
          )}

          {/* ── Step 3 — Success ── */}
          {step === 'success' && result && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="p-6 text-center space-y-5"
            >
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 220, damping: 16 }}
                className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </motion.div>

              <div>
                <h3 className="text-lg font-bold text-slate-900">Device registered</h3>
                <p className="text-sm text-slate-500 mt-1">M8 light controller recorded successfully.</p>
              </div>

              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-left">
                <div className="px-4 py-3">
                  <p className="text-xs text-slate-400 mb-1">Registration ID</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono text-xs text-slate-700 break-all leading-relaxed">{result.id}</p>
                    <CopyButton value={result.id} label="Registration ID copied!" />
                  </div>
                </div>
                <div className="px-4 py-3">
                  <p className="text-xs text-slate-400 mb-1.5">Status</p>
                  <StatusBadge status={result.status} />
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="secondary" fullWidth onClick={handleReset}>Register another</Button>
                <Button variant="primary" fullWidth onClick={() => navigate('/devices')}>
                  View Device List
                </Button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="px-4 py-3">
      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
      <p className={`text-sm font-semibold text-slate-900 break-words ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}
